/**
 * The rater's questionnaire.
 *
 * Answers are held locally and flushed to the API on a short debounce, so the
 * survey feels instant and a closed tab loses at most the last second of work.
 * Navigating always flushes first — leaving a screen is the moment a rater is
 * most likely to close the browser.
 */
import { create } from 'zustand';
import type { ResponseValue, SurveyDto } from '@genoma/shared';

import {
  fetchCurrentSurvey,
  saveAnswers,
  saveOpenComment,
  submitSurvey,
} from '../services/survey.service.ts';

const AUTOSAVE_DELAY_MS = 800;

/**
 * Pause between placing the last mark on a screen and moving to the next one.
 *
 * Long enough to see the mark land and the anchor flash begin, short enough
 * that it reads as a consequence of the click rather than a delay.
 */
const AUTO_ADVANCE_DELAY_MS = 700;

/**
 * Pause before writing an open answer.
 *
 * Longer than the one for marks: this is prose, and saving on every short
 * pause would be a request every few words.
 */
const COMMENT_AUTOSAVE_DELAY_MS = 1500;

/** Answers are keyed by the pair they belong to. */
const answerKey = (evaluatedId: string, behaviorId: string) => `${evaluatedId}:${behaviorId}`;

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

type SurveyState = {
  survey: SurveyDto | null;
  status: 'loading' | 'ready' | 'error' | 'submitted';
  errorMessage: string | null;

  /**
   * The open question comes after all twelve behaviors, on a screen of its own,
   * rather than woven into the sequence. Two reasons: the rater has to have
   * seen the whole person before they can write about them, and keeping it out
   * of the screen sequence leaves the completeness rules — which govern the
   * scored answers and only those — exactly as they were.
   */
  view: 'intro' | 'question' | 'comments' | 'done';
  behaviorIndex: number;
  groupIndex: number;

  answers: Record<string, ResponseValue>;
  /** The open answer per evaluated person. Optional, and often empty. */
  comments: Record<string, string>;
  saveState: SaveState;
  /**
   * Set when finishing was blocked by unanswered questions, with how many are
   * missing. The rater is taken to the first gap rather than told a number.
   */
  missingAnswers: number | null;
  completion: { answered: number; notObserved: number } | null;
  /**
   * The last mark placed, used to flash the matching anchor card. The nonce
   * makes repeated identical answers retrigger the animation.
   */
  lastMark: { value: ResponseValue; nonce: number } | null;
  /**
   * The rater is being carried to the next screen and has nothing to press.
   *
   * True from the moment the last mark on a screen lands until the next screen
   * is up, and for the length of any navigation. The questionnaire moves by
   * itself; the way forward only appears when it will not — on the last screen,
   * and for somebody who went back and is now reading rather than answering.
   */
  advancing: boolean;

  load: () => Promise<void>;
  begin: () => void;
  answer: (evaluatedId: string, behaviorId: string, value: ResponseValue) => void;
  writeComment: (evaluatedId: string, text: string) => void;
  submit: () => Promise<void>;
  valueFor: (evaluatedId: string, behaviorId: string) => ResponseValue | undefined;
  goNext: () => Promise<void>;
  goBack: () => Promise<void>;
  flush: () => Promise<void>;
};

/**
 * Answers waiting to be written. Kept outside the store because they are
 * transport state, not something a component should re-render on.
 */
let pending = new Map<string, { evaluatedId: string; behaviorId: string; value: ResponseValue }>();
/** Open answers waiting to be written, keyed by the person they are about. */
let pendingComments = new Map<string, string>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let commentTimer: ReturnType<typeof setTimeout> | null = null;
let advanceTimer: ReturnType<typeof setTimeout> | null = null;

const cancelAdvance = () => {
  if (advanceTimer) {
    clearTimeout(advanceTimer);
    advanceTimer = null;
  }
};

/**
 * One navigation at a time.
 *
 * `goNext` reads the position it is moving from *after* awaiting the save, so
 * two overlapping calls each read a position the other has already moved: the
 * rater travels two screens on one intention, skipping a behavior they then
 * meet at the end as a gap they do not remember leaving.
 *
 * That is exactly what a second tap on Next did while the first was still
 * saving — the screen had not changed yet, so tapping again was the reasonable
 * thing to do. Hiding the button during the wait makes it unlikely; this makes
 * it impossible, which is what the answers deserve.
 */
let navigating = false;

export const useSurveyStore = create<SurveyState>((set, get) => {
  /**
   * Write the open answers. Separate from the answer flush because they go to
   * their own endpoint, but always awaited alongside it, so leaving the page
   * never loses a sentence somebody just typed.
   */
  const flushComments = async (): Promise<void> => {
    if (commentTimer) {
      clearTimeout(commentTimer);
      commentTimer = null;
    }
    const { survey } = get();
    if (!survey || pendingComments.size === 0) return;

    const batch = [...pendingComments.entries()];
    pendingComments = new Map();

    for (const [evaluatedId, text] of batch) {
      try {
        await saveOpenComment(survey.id, evaluatedId, text);
      } catch {
        // Put it back only if nothing newer has been typed meanwhile.
        if (!pendingComments.has(evaluatedId)) pendingComments.set(evaluatedId, text);
        set({ saveState: 'error' });
      }
    }
  };

  const flush = async (): Promise<void> => {
    if (flushTimer) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }
    await flushComments();
    const { survey } = get();
    if (!survey || pending.size === 0) return;

    const batch = [...pending.values()];
    pending = new Map();
    set({ saveState: 'saving' });

    const { behaviorIndex, groupIndex, survey: current } = get();
    try {
      await saveAnswers(survey.id, batch, {
        behaviorNumber: current?.behaviors[behaviorIndex]?.number,
        groupIndex,
      });
      set({ saveState: 'saved' });
    } catch {
      // Put the batch back so the next flush retries it rather than losing it.
      for (const answer of batch) {
        const key = answerKey(answer.evaluatedId, answer.behaviorId);
        if (!pending.has(key)) pending.set(key, answer);
      }
      set({ saveState: 'error' });
    }
  };

  const scheduleFlush = () => {
    if (flushTimer) clearTimeout(flushTimer);
    flushTimer = setTimeout(() => void flush(), AUTOSAVE_DELAY_MS);
  };

  /**
   * Record where the rater now is, with no answers attached.
   *
   * Position used to ride along with answers, which meant returning landed on
   * the last screen they *answered on* rather than the one they were looking
   * at. Fire-and-forget: losing a position is a minor inconvenience, and it
   * must never block navigation.
   */
  const syncPosition = () => {
    const { survey, behaviorIndex, groupIndex } = get();
    if (!survey || survey.status === 'SUBMITTED') return;
    void saveAnswers(survey.id, [], {
      behaviorNumber: survey.behaviors[behaviorIndex]?.number,
      groupIndex,
    }).catch(() => undefined);
  };

  return {
    survey: null,
    status: 'loading',
    errorMessage: null,
    view: 'intro',
    behaviorIndex: 0,
    groupIndex: 0,
    answers: {},
    comments: {},
    saveState: 'idle',
    completion: null,
    lastMark: null,
    missingAnswers: null,
    advancing: false,

    load: async () => {
      cancelAdvance();
      // A lock is only ever held for the length of one navigation, but it is
      // module state and a fresh questionnaire must not inherit one: a lock
      // left set would refuse every move the rater made, forever.
      navigating = false;
      set({ status: 'loading', errorMessage: null, advancing: false });
      try {
        const survey = await fetchCurrentSurvey();

        const answers: Record<string, ResponseValue> = {};
        for (const answer of survey.answers) {
          answers[answerKey(answer.evaluatedId, answer.behaviorId)] = answer.value;
        }

        // Someone coming back mid-survey has already read the intro and wants
        // the screen they left, not behavior one. Someone arriving for the
        // first time gets the intro, which carries the whole reframe.
        const returning = survey.status !== 'SUBMITTED' && survey.answers.length > 0;
        const resumeIndex = survey.resumeAt.behaviorNumber
          ? Math.max(
              survey.behaviors.findIndex(
                (behavior) => behavior.number === survey.resumeAt.behaviorNumber,
              ),
              0,
            )
          : 0;
        const resumeGroup = Math.min(
          survey.resumeAt.groupIndex ?? 0,
          Math.max(survey.screens.length - 1, 0),
        );

        set({
          survey,
          answers,
          comments: { ...survey.openComments },
          status: survey.status === 'SUBMITTED' ? 'submitted' : 'ready',
          view:
            survey.status === 'SUBMITTED' ? 'done' : returning ? 'question' : 'intro',
          behaviorIndex: returning ? resumeIndex : 0,
          groupIndex: returning ? resumeGroup : 0,
        });
      } catch (error) {
        set({
          status: 'error',
          errorMessage: error instanceof Error ? error.message : null,
        });
      }
    },

    begin: () => {
      cancelAdvance();
      set({ view: 'question', behaviorIndex: 0, groupIndex: 0, advancing: false });
    },

    answer: (evaluatedId, behaviorId, value) => {
      const wasComplete = screenComplete(get());

      set((state) => ({
        answers: { ...state.answers, [answerKey(evaluatedId, behaviorId)]: value },
        lastMark: { value, nonce: (state.lastMark?.nonce ?? 0) + 1 },
      }));
      pending.set(answerKey(evaluatedId, behaviorId), { evaluatedId, behaviorId, value });
      scheduleFlush();

      // Move on by itself when that mark was the one the screen was waiting
      // for. Only on the transition into completeness: someone who came back
      // to change an answer on a finished screen is reviewing, not advancing,
      // and being thrown forward would fight them.
      cancelAdvance();
      if (!wasComplete && screenComplete(get()) && !onLastScreen(get())) {
        // What the screen watches: while this is true the rater is being
        // carried forward, and there is nothing for them to press.
        set({ advancing: true });
        advanceTimer = setTimeout(() => {
          advanceTimer = null;
          // Re-check: they may have navigated or changed their mind meanwhile.
          if (screenComplete(get()) && !onLastScreen(get())) void get().goNext();
          else set({ advancing: false });
        }, AUTO_ADVANCE_DELAY_MS);
      } else {
        // Changing an answer on a screen that was already finished is
        // reviewing. Nothing moves, so the way forward stays on screen.
        set({ advancing: false });
      }
    },

    valueFor: (evaluatedId, behaviorId) => get().answers[answerKey(evaluatedId, behaviorId)],

    writeComment: (evaluatedId, text) => {
      set((state) => ({ comments: { ...state.comments, [evaluatedId]: text } }));
      pendingComments.set(evaluatedId, text);
      if (commentTimer) clearTimeout(commentTimer);
      // A longer debounce than the answers use: this is prose, and a request
      // per keystroke-pause would be a request every few words.
      commentTimer = setTimeout(() => void flushComments(), COMMENT_AUTOSAVE_DELAY_MS);
    },

    /**
     * Send the questionnaire. Reached from the writing screen, never from a
     * behavior screen: submitting stays the last deliberate act.
     */
    submit: async () => {
      cancelAdvance();
      set({ advancing: false });
      await flush();
      const { survey } = get();
      if (!survey) return;

      try {
        const completion = await submitSurvey(survey.id);
        set({ view: 'done', status: 'submitted', completion, missingAnswers: null });
      } catch (error) {
        set({
          saveState: 'error',
          errorMessage: error instanceof Error ? error.message : null,
        });
      }
    },

    /**
     * Advance one screen. In the multiple format a behavior spans several
     * screens of people, so the group advances before the behavior does.
     */
    goNext: async () => {
      if (navigating) return;
      cancelAdvance();
      // Never advance off an unfinished screen. The button is hidden or
      // disabled for this, but the auto-advance timer can also reach here, and
      // a guard at the source is what makes gaps impossible rather than
      // unlikely.
      if (!screenComplete(get()) && !onLastScreen(get())) {
        set({ advancing: false });
        return;
      }

      navigating = true;
      set({ advancing: true });
      try {
        await flush();
        const { survey, behaviorIndex, groupIndex } = get();
        if (!survey) return;

        const lastGroup = Math.max(survey.screens.length - 1, 0);
        const lastBehavior = survey.behaviors.length - 1;

        if (survey.format === 'MULTIPLE' && groupIndex < lastGroup) {
          set({ groupIndex: groupIndex + 1, lastMark: null, missingAnswers: null });
          syncPosition();
          return;
        }

        if (behaviorIndex < lastBehavior) {
          set({
            behaviorIndex: behaviorIndex + 1,
            groupIndex: 0,
            lastMark: null,
            missingAnswers: null,
          });
          syncPosition();
          return;
        }

        // Finishing is the one moment the whole questionnaire has to be complete,
        // and a screen-by-screen guard cannot see a gap left three screens back.
        // Rather than let the API reject the submission, find the gap and take
        // them to it.
        const gap = firstIncompleteScreen(get());
        if (gap) {
          set({
            behaviorIndex: gap.behaviorIndex,
            groupIndex: gap.groupIndex,
            missingAnswers: gap.missing,
            lastMark: null,
          });
          syncPosition();
          return;
        }

        // Everything scored is answered. What is left is the open question, and
        // it is optional — so this screen leads to it rather than submitting.
        set({ view: 'comments', missingAnswers: null, lastMark: null });
      } finally {
        navigating = false;
        set({ advancing: false });
      }
    },

    goBack: async () => {
      if (navigating) return;
      navigating = true;
      cancelAdvance();
      try {
        await flush();
        const { survey, behaviorIndex, groupIndex, view } = get();
        if (!survey) return;

        // Back out of the writing screen onto the last behavior, so a rater who
        // wants to change an answer can still reach it.
        if (view === 'comments') {
          set({ view: 'question', lastMark: null });
          return;
        }

        if (groupIndex > 0) {
          set({ groupIndex: groupIndex - 1, lastMark: null });
          syncPosition();
          return;
        }
        if (behaviorIndex > 0) {
          const previousGroups = survey.format === 'MULTIPLE' ? survey.screens.length - 1 : 0;
          set({ behaviorIndex: behaviorIndex - 1, groupIndex: previousGroups, lastMark: null });
          syncPosition();
        }
      } finally {
        navigating = false;
        // Going back always lands on a screen already answered, so the way
        // forward appears there — which is how somebody who came back to
        // re-read gets to the front again without touching an answer.
        set({ advancing: false });
      }
    },

    flush,
  };
});

/**
 * True when this is the final screen of the questionnaire — the one whose
 * button submits. Submitting stays a deliberate act, so nothing auto-advances
 * off this screen.
 */
export const onLastScreen = (state: SurveyState): boolean => {
  const { survey, behaviorIndex, groupIndex } = state;
  if (!survey) return true;
  const lastBehavior = behaviorIndex === survey.behaviors.length - 1;
  const lastGroup =
    survey.format === 'INDIVIDUAL' || groupIndex === survey.screens.length - 1;
  return lastBehavior && lastGroup;
};

/** The screen the rater is on, with the group it represents. */
export const currentScreen = (state: SurveyState) =>
  state.survey?.screens[state.groupIndex] ?? null;

/** The people shown on the current screen. */
export const currentPeople = (state: SurveyState) => {
  const { survey } = state;
  if (!survey) return [];
  if (survey.format === 'INDIVIDUAL') return survey.evaluated;

  const ids = currentScreen(state)?.participantIds ?? [];
  return ids
    .map((id) => survey.evaluated.find((person) => person.id === id))
    .filter((person): person is NonNullable<typeof person> => Boolean(person));
};

/**
 * True when this screen assesses a different role level than the one before,
 * which means the three anchors now describe different behaviour. Easy to miss
 * mid-questionnaire, so the interface calls it out.
 */
export const roleLevelJustChanged = (state: SurveyState): boolean => {
  const { survey, groupIndex } = state;
  if (!survey || survey.format !== 'MULTIPLE' || groupIndex === 0) return false;
  return survey.screens[groupIndex]?.roleLevel !== survey.screens[groupIndex - 1]?.roleLevel;
};

/**
 * The first screen with an unanswered person, scanning the questionnaire in
 * the order the rater walks it.
 *
 * Returns the screen's coordinates and how many answers are missing overall,
 * so "you are not finished" can become "here is where".
 */
export const firstIncompleteScreen = (
  state: SurveyState,
): { behaviorIndex: number; groupIndex: number; missing: number } | null => {
  const { survey, answers } = state;
  if (!survey) return null;

  const screensOf = (): string[][] =>
    survey.format === 'INDIVIDUAL'
      ? [survey.evaluated.map((person) => person.id)]
      : survey.screens.map((screen) => screen.participantIds);

  const screens = screensOf();
  let missing = 0;
  let gapBehavior = -1;
  let gapGroup = -1;

  survey.behaviors.forEach((behavior, behaviorIndex) => {
    screens.forEach((participantIds, groupIndex) => {
      for (const participantId of participantIds) {
        if (answers[answerKey(participantId, behavior.id)] !== undefined) continue;
        missing += 1;
        if (gapBehavior === -1) {
          gapBehavior = behaviorIndex;
          gapGroup = groupIndex;
        }
      }
    });
  });

  return gapBehavior === -1
    ? null
    : { behaviorIndex: gapBehavior, groupIndex: gapGroup, missing };
};

/** Whether every person on this screen has been answered for this behavior. */
export const screenComplete = (state: SurveyState): boolean => {
  const behavior = state.survey?.behaviors[state.behaviorIndex];
  if (!behavior) return false;
  return currentPeople(state).every(
    (person) => state.answers[answerKey(person.id, behavior.id)] !== undefined,
  );
};

export const answeredCount = (state: SurveyState): number =>
  Object.keys(state.answers).length;
